import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs/operators';
import { ContractorsSelectorComponent } from 'src/app/contractors/contractors/contractors-selector/contractors-selector.component';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { SsspTabsDialogComponent } from 'src/app/sssp/sssp-tabs/sssp-tabs-dialog.component';
import { AppService } from '../../app.service';
import { UtilsService } from '../../shared/utils.service';
import { CustomDataSource } from '../../utils/custom-data-source';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-sites-contractors',
  templateUrl: './sites-contractors.component.html',
  styleUrls: ['./sites-contractors.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesContractorsComponent implements OnInit, AfterViewInit {
  // the site id must be present
  parent_id: number;
  child_id: number;
  site_id: number;

  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'contact_person',
    'entity_name',
    'email',
    'mobile',
    'actions'
  ];
  dataSource: ContractorsDataSource;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    public route: ActivatedRoute,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    // Check permissions: (sites:view + contractors:view) OR any F_SITES_SAFETY_PLANS
    const hasParentPermissions = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    const hasAnySafetyPlanPermission = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
        RBACService.RBAC_A.A_CREATE
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
        RBACService.RBAC_A.A_EDIT
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
        RBACService.RBAC_A.A_DELETE
      )
    ]);

    if (!hasParentPermissions && !hasAnySafetyPlanPermission) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view site contractors.'
      );
      this.utils.redirectByLastSegment('details');
      return;
    }

    // Conditionally set displayed columns based on permissions
    if (this.canEditContractors()) {
      this.displayedColumns = [
        'select',
        'contact_person',
        'entity_name',
        'email',
        'mobile',
        'actions'
      ];
    } else {
      this.displayedColumns = [
        'contact_person',
        'entity_name',
        'email',
        'mobile',
        'actions'
      ];
    }

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;

      // load a multi select data source object
      this.dataSource = new ContractorsDataSource(this.app, this.api, true, {
        site_id: this.site_id
      });

      // get the data only when the site id is present
      this.dataSource.getData();
    }
  }

  /**
   * Check if user can link/unlink contractors
   */
  canEditContractors(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
  }

  /**
   * Check if user can create contractor safety plans (sites perspective)
   */
  canCreateSafetyPlans(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
  }

  /**
   * Check if user can edit contractor safety plans (sites perspective)
   */
  canEditSafetyPlans(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
  }

  /**
   * Check if user can send messages
   */
  canSendMessages(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_MESSAGES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
  }

  /**
   * Check if list menu should be shown
   * Menu is only useful if user can edit contractors or send messages
   */
  shouldShowListMenu(): boolean {
    return this.canEditContractors() || this.canSendMessages();
  }

  ngAfterViewInit() {
    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   *
   * @param id the contractor id
   *
   * unlink a single contractor
   */
  onRemove(id: number) {
    // Check permission before showing confirmation
    if (!this.canEditContractors()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink contractors.'
      );
      return;
    }

    this.utils.showModal(
      `Unlink ${this.utils.getLangTerm('contractors.singular', 'Contractor')}`,
      `Are you sure you want to unlink this ${this.utils.getLangTerm(
        'contractors.singular',
        'Contractor'
      )}?`,
      () => {
        this.onUpdateLinkedContractors(
          this.dataSource.contractor_ids.filter(
            (contractor_id) => ![id].includes(contractor_id)
          ),
          (response) => {
            this.dataSource.selection.deselect(id);
          }
        );
      }
    );
  }

  /**
   * unlink selected contractors from the specified site
   */
  onDeleteSelected() {
    // Check permission before showing confirmation
    if (!this.canEditContractors()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink contractors.'
      );
      return;
    }

    this.utils.showModal(
      `Unlink Selected ${this.utils.getLangTerm(
        'contractors.plural',
        'Contractors'
      )}`,
      `Are you sure you want to unlink the selected ${this.utils.getLangTerm(
        'contractors.plural',
        'Contractors'
      )}?`,
      () => {
        this.onUpdateLinkedContractors(
          this.dataSource.contractor_ids.filter(
            (contractor_id) =>
              !this.dataSource.selection.selected.includes(contractor_id)
          ),
          (response) => {
            this.dataSource.selection.clear();
          }
        );
      }
    );
  }

  /**
   * open a dialog allowing the user to select contractors to be linked to sites.
   */
  onLinkContractorBusiness() {
    // Check permission before opening dialog
    if (!this.canEditContractors()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to link contractors.'
      );
      return;
    }

    // show the site selector
    this.utils.showComponentDialog(
      ContractorsSelectorComponent,
      {
        multiple: true,
        selected: this.dataSource.contractor_ids
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          this.dataSource.contractor_ids = results;

          if (this.site_id) {
            this.onUpdateLinkedContractors(this.dataSource.contractor_ids);
          }
        }
      }
    );
  }

  onUpdateLinkedContractors(contractor_ids: any, callback?: any) {
    // Check permission before proceeding with update.
    if (!this.canEditContractors()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to link contractors.'
      );
      return;
    }

    // Check if the site id is present and valid.
    if (this.site_id) {
      // link all contractor businesses
      this.api
        .makeRequest(
          'put',
          'v1/sites/' + this.site_id + '/linked-contractors',
          {
            selected_contractor_ids: contractor_ids
          }
        )
        .then((response: any) => {
          // reload the data and reset the offset
          this.dataSource.getData(true);

          if (typeof callback === 'function') {
            callback(response);
          }
        })
        .catch((err: any) => {
          this.utils.showModal('Error', err.error.message);
        });
    }
  }

  composeNewMessage() {
    // Check permission before composing message
    if (!this.canSendMessages()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to send messages.'
      );
      return;
    }

    if (this.dataSource.selection.selected.length == 0) {
      this.utils.showToast(
        `You need to select some ${this.utils.getLangTerm(
          'contractors.plural',
          'Contractors'
        )} first.`
      );
      return; // early termination
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectAccountIds: this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  onCreateSSSP(contractor_id: number) {
    // Check permission before creating
    if (!this.canCreateSafetyPlans()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create safety plans.'
      );
      return;
    }

    // Make a post request to create a new Safety Plan.
    // Reload the list to show the latest changes.
    this.api
      .makeRequest('post', 'v1/sssp', {
        site_id: this.site_id,
        contractor_id: contractor_id
      })
      .then((response: any) => {
        // This means the request was successful.
        const safety_plan_term = this.utils.getLangTerm(
          'safety-plans.singular',
          'Safety Plan'
        );

        // Check if user has edit permission before opening dialog
        if (this.canEditSafetyPlans()) {
          // User can edit - open the dialog
          this.onManageSSSP(response.sssp_hash);
        } else {
          // User can only create, not edit - show success message
          this.utils.showToast(
            `The ${safety_plan_term} was created successfully. You need edit permissions to manage it.`
          );
        }

        // Reload the list in the background.
        this.dataSource.getData();
      })
      .catch((error) => {
        const safety_plan_term = this.utils.getLangTerm(
          'safety-plans.singular',
          'Safety Plan'
        );
        this.utils.showToast(
          `The ${safety_plan_term} was created successfully.`
        );
      });
  }

  onManageSSSP(sssp_hash: string) {
    // Check permission before opening dialog
    if (!this.canEditSafetyPlans()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit safety plans.'
      );
      return;
    }

    // Open the Safety Plan manager using a dialog window.
    this.utils.showComponentDialog(
      SsspTabsDialogComponent,
      {
        sssp_hash: sssp_hash
      },
      {
        width: '1200px'
      }
    );
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

/**
 * the data source class should contain everything needed to get data for the feature.
 * this only applies to lists. forms will use direct calls to the api.
 */
export class ContractorsDataSource extends CustomDataSource {
  sort_by = 'entity_name';
  sort_order = 'asc';

  contractor_ids: number[] = [];

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'sites/' + this['site_id'] + '/linked-contractors',
      resetOffset,
      (response) => {
        this.contractor_ids = response.meta.contractor_ids;
      }
    );
  }
}
